import { useState, useEffect } from 'react';
import useForceUpdate from './useForceUpdate';

// ============================================================================
// Types
// ============================================================================

export interface UseFetchReturn<T> {
  /** Fetched data */
  data: T | null;
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: Error | null;
  /** Manually trigger fetch */
  refetch: () => void;
}

export interface UseFetchOptions<T> {
  /** Query key array - used to track and refetch when it changes */
  queryKey: unknown[];
  /** Fetch function - required when enabled is true */
  queryFn?: (abortController: AbortController) => Promise<T>;
  /** Called when loading starts */
  onLoading?: () => void;
  /** Called when loading ends */
  onLoaded?: () => void;
  /** Called on error */
  onError?: (error: Error) => void;
  /** Auto-fetch when queryKey changes (default: true) */
  enabled?: boolean;
}

// ============================================================================
// Hook
// ============================================================================

function useFetch<T>({
  queryKey,
  queryFn,
  onLoading,
  onLoaded,
  onError,
  enabled = true,
}: UseFetchOptions<T>): UseFetchReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchTrigger, refetch] = useForceUpdate();

  // Single place for fetch execution and AbortController management
  useEffect(() => {
    if (!enabled || !queryFn) return;

    const abortController = new AbortController();

    const executeFetch = async () => {
      setLoading(true);
      setError(null);
      onLoading?.();

      try {
        const result = await queryFn(abortController);
        if (!abortController.signal.aborted) {
          setData(result);
        }
      } catch (err) {
        if (!abortController.signal.aborted) {
          const error = err instanceof Error ? err : new Error(String(err));
          setError(error);
          onError?.(error);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
          onLoaded?.();
        }
      }
    };

    executeFetch();

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, fetchTrigger, ...queryKey]);

  return { data, loading, error, refetch };
}

export default useFetch;
